/**
 * Loan-term input with a months/years unit toggle.
 *
 * The term is always carried as a canonical month count (`months`); the `unit`
 * is purely a display/entry choice:
 *   - "months" → a single number box (e.g. 12 mo).
 *   - "years"  → a years box + a leftover-months box, so mixed terms like
 *     "30 years 6 months" round-trip losslessly (30y6m = 366 months).
 *
 * Every edit reports the new canonical month count back via `onChange` so the
 * model never has to guess the unit. See `termInMonths()` / `monthsToYearsMonths()`
 * in `@/lib/calculators`.
 */
import { NumberInput } from "@/components/ui/number-input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { monthsToYearsMonths } from "@/lib/calculators";
import { cn } from "@/lib/utils";

export type TermUnit = "months" | "years";

interface TermFieldProps {
  /** Canonical term length in months — the source of truth. */
  months: number;
  /** Display/entry unit. */
  unit: TermUnit;
  /** Fires with the new canonical months + the (possibly changed) unit. */
  onChange: (next: { months: number; unit: TermUnit }) => void;
  /** Extra classes for each number input (height/background to match the host). */
  inputClassName?: string;
  /** Extra classes for the unit Select trigger. */
  triggerClassName?: string;
}

export function TermField({
  months,
  unit,
  onChange,
  inputClassName,
  triggerClassName,
}: TermFieldProps) {
  const safeMonths = Number.isFinite(months) ? Math.max(0, months) : 0;
  const { years, months: leftover } = monthsToYearsMonths(safeMonths);

  const emitYears = (y: number, mo: number) =>
    onChange({
      months: Math.max(0, Math.round(y) * 12 + Math.round(mo)),
      unit: "years",
    });

  const emitMonths = (mo: number) =>
    onChange({ months: Math.max(0, Math.round(mo)), unit: "months" });

  const handleUnitChange = (next: TermUnit) =>
    onChange({ months: safeMonths, unit: next });

  const inputCls = cn("min-w-0 font-bold", inputClassName);

  return (
    <div className="flex gap-1.5">
      {unit === "years" ? (
        <>
          <NumBox
            value={years}
            onChange={(v) => emitYears(v, leftover)}
            suffix="yr"
            ariaLabel="Term years"
            className={inputCls}
          />
          {/* The leftover-months box is capped at 11, so it never needs more
              than two digits — give it a fixed width instead of an equal
              share, and let the years box take the rest. Splitting this row
              evenly left the years box too narrow to show a 3-digit term. */}
          <NumBox
            value={leftover}
            onChange={(v) => emitYears(years, v)}
            suffix="mo"
            ariaLabel="Term months"
            max={11}
            className={inputCls}
            wrapperClassName="w-[68px] shrink"
          />
        </>
      ) : (
        <NumBox
          value={safeMonths}
          onChange={emitMonths}
          suffix="mo"
          ariaLabel="Term months"
          className={inputCls}
        />
      )}
      <Select value={unit} onValueChange={(v) => handleUnitChange(v as TermUnit)}>
        <SelectTrigger
          className={cn("w-[62px] shrink-0", triggerClassName)}
          aria-label="Term unit"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="months">mo</SelectItem>
          <SelectItem value="years">yr</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}

interface NumBoxProps {
  value: number;
  onChange: (v: number) => void;
  suffix: string;
  ariaLabel: string;
  max?: number;
  className?: string;
  /**
   * Width behaviour for the box. Replaces (not layers onto) the default equal
   * share — `flex-1`'s shorthand would otherwise reset any `basis` override.
   */
  wrapperClassName?: string;
}

function NumBox({
  value,
  onChange,
  suffix,
  ariaLabel,
  max,
  className,
  wrapperClassName,
}: NumBoxProps) {
  return (
    <div className={cn("relative min-w-0", wrapperClassName ?? "flex-1")}>
      <NumberInput
        min={0}
        max={max}
        step={1}
        value={Number.isFinite(value) ? value : 0}
        onChange={onChange}
        aria-label={ariaLabel}
        className={cn(
          // The suffix sits in the right padding; `pr-7` is the smallest gutter
          // that clears a two-character unit. Native spinners are hidden so
          // Chrome's arrows can't overlap the digits on hover/focus.
          "pr-7 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none",
          className,
        )}
      />
      <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-xs font-bold text-muted-foreground">
        {suffix}
      </span>
    </div>
  );
}
